import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Database, Send, CheckCircle2, Layers, ArrowRight } from "lucide-react";
import { useUserContext } from "../../../contexts/UserContext";
import { useContentfulContext } from "../../../contexts/ContentfulContext";
import { getSetupConfigs, updateSetupConfig } from "../../../utils/APIs";
import Toggle from '../../../components/UI/Toggle';
import { toast } from "react-toastify";
import Loader from '../../../components/UI/Loader/Loader';
import { permission } from "../../../config/permission";
import Card from "../../../components/UI/Card";
import Button from "../../../components/UI/Button";

const getProviderMeta = (name = "") => {
    const lower = name.toLowerCase();
    if (lower.includes("adobe") || lower.includes("target")) {
        return { badge: "AT", color: "bg-rose-50 text-rose-600 border-rose-200" };
    }
    if (lower.includes("contentful")) {
        return { badge: "CF", color: "bg-blue-50 text-blue-600 border-blue-200" };
    }
    if (lower.includes("tealium")) {
        return { badge: "TM", color: "bg-teal-50 text-teal-600 border-teal-200" };
    }
    if (lower.includes("github")) {
        return { badge: "GH", color: "bg-slate-100 text-slate-800 border-slate-300" };
    }
    if (lower.includes("aem")) {
        return { badge: "AEM", color: "bg-purple-50 text-purple-600 border-purple-200" };
    }
    return { badge: name.slice(0, 2).toUpperCase(), color: "bg-primary-soft text-primary border-primary/20" };
};

const Setup = () => {
    const { userData, updateSelectedOrgData, checkPermission } = useUserContext();
    const { content } = useContentfulContext();

    const [data, setData] = useState({ sources: [], destinations: [] });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        getAllSourcesAndDestinations();
    }, []);

    const getAllSourcesAndDestinations = async () => {
        try {
            setIsLoading(true);
            const result = await getSetupConfigs(userData?.token, userData?.selectedOrganization?.org_token);
            setData(result.data || { sources: [], destinations: [] });
        } catch (error) {
            toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOnChangeSource = async (item, index) => {
        if (!checkPermission(permission.SETUP_UPDATE)) {
            return;
        }
        const sources = [...data.sources];
        sources[index].enabled = !sources[index].enabled;
        const updatedVal = { ...data, sources };
        setData(updatedVal);
        const payload = {
            sources: updatedVal.sources.filter((x) => x.enabled).map((y) => y.id),
            destinations: updatedVal.destinations.filter((x) => x.enabled).map((y) => y.id)
        };
        await handleSaveSetupConfiguration(payload);
    };

    const handleOnChangeDestination = async (item, index) => {
        if (!checkPermission(permission.SETUP_UPDATE)) {
            return;
        }
        const destinations = [...data.destinations];
        destinations[index].enabled = !destinations[index].enabled;
        const updatedVal = { ...data, destinations };
        setData(updatedVal);
        const payload = {
            sources: updatedVal.sources.filter((x) => x.enabled).map((y) => y.id),
            destinations: updatedVal.destinations.filter((x) => x.enabled).map((y) => y.id)
        };
        await handleSaveSetupConfiguration(payload);
    };

    const handleSaveSetupConfiguration = async (payload) => {
        try {
            setIsLoading(true);
            const res = await updateSetupConfig(userData?.token, userData?.selectedOrganization?.org_token, payload);
            await getAllSourcesAndDestinations();
            await updateSelectedOrgData();
            toast.success(res?.message);
        } catch (error) {
            toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setIsLoading(false);
        }
    };

    const activeSourcesCount = (data.sources?.filter(s => s.enabled)?.length || 0) + 1;
    const activeDestinationsCount = (data.destinations?.filter(d => d.enabled)?.length || 0) + 1;

    return (
        <main id="main" className="stride-page-shell flex flex-col space-y-6">
            {isLoading && <Loader />}

            {/* Header */}
            <div>
                <span className="stride-eyebrow">Settings / Integrations</span>
                <h1 className="text-2xl font-bold tracking-tight text-stride-heading mt-0.5">
                    {content.SETUP_DASHBOARD_CONTENT.welcome_message}
                </h1>
                <p className="text-sm text-stride-muted mt-1">
                    Connect and manage your data sources and delivery destinations to automate experience workflows.
                </p>
            </div>

            {/* Flow architecture overview banner */}
            <div className="bg-surface-subtle/80 border border-border-subtle rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs text-stride-muted shadow-xs">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary-soft text-primary border border-primary/20 flex items-center justify-center font-bold shrink-0 shadow-xs">
                        <Layers size={17} />
                    </div>
                    <div>
                        <p className="font-bold text-stride-heading text-sm">Experience Integration Pipeline</p>
                        <p className="text-stride-muted text-xs">Route inbound content repositories through Stride into connected delivery destinations.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 font-medium shrink-0 self-start sm:self-auto">
                    <span className="px-2.5 py-1 rounded-md bg-white border border-border-subtle text-stride-heading font-semibold text-xs shadow-xs">
                        {activeSourcesCount} Sources Active
                    </span>
                    <ArrowRight size={13} className="text-stride-muted" />
                    <span className="px-2.5 py-1 rounded-md bg-white border border-border-subtle text-stride-heading font-semibold text-xs shadow-xs">
                        {activeDestinationsCount} Destinations Active
                    </span>
                </div>
            </div>

            {/* Grid for Sources and Destinations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

                {/* Sources Column */}
                <Card variant="default" padding="none" className="border border-border-subtle shadow-card flex flex-col p-6 bg-white">
                    <div className="flex items-center justify-between pb-4 mb-4 border-b border-border-subtle/80">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary-soft border border-primary/20 text-primary flex items-center justify-center shadow-xs">
                                <Database className="w-5 h-5 text-current" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-stride-heading">
                                    {content.SETUP_DASHBOARD_CONTENT.sources}
                                </h2>
                                <p className="text-xs text-stride-muted">
                                    Content repositories and data layers
                                </p>
                            </div>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-soft text-primary border border-primary/20">
                            {activeSourcesCount} Active
                        </span>
                    </div>

                    <div className="flex flex-col gap-3">
                        {/* Manual Source (Fixed / Disabled) */}
                        <div
                            className="transition-all duration-150 flex items-center justify-between gap-4"
                            aria-disabled="true"
                        >
                            <div className="flex items-center gap-3.5">
                                <div className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 border bg-slate-100 text-slate-700 border-slate-200">
                                    MN
                                </div>
                                <div>
                                    <p className="capitalize font-semibold text-stride-heading text-sm">
                                        {content.SETUP_DASHBOARD_CONTENT.manual}
                                    </p>
                                    <span className="text-xs text-stride-muted">
                                        Built-in direct data entry
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/80">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                    Always On
                                </span>
                                <div id="source-wrapper-">
                                    <Toggle
                                        id="source-"
                                        checked={true}
                                        disabled={true}
                                        ariaLabel="Manual source is always active"
                                        title="Manual source is always active"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Dynamic Sources */}
                        {data.sources?.map((x, index) => {
                            const meta = getProviderMeta(x.name);
                            return (
                                <div
                                    key={x.id}
                                    data-testid={`source-card-${x.name.toLowerCase().replace(/\s+/g, '-')}`}
                                    className={`transition-all duration-150 flex items-center justify-between gap-4`}
                                >
                                    <div className="flex items-center gap-3.5 min-w-0">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 border ${meta.color}`}>
                                            {meta.badge}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="capitalize font-semibold text-stride-heading text-sm truncate">
                                                {x.name}
                                            </p>
                                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold mt-0.5 ${
                                                x.enabled ? 'text-emerald-700' : 'text-stride-muted'
                                            }`}>
                                                <span className={`w-2 h-2 rounded-full ${x.enabled ? 'bg-emerald-500 shadow-xs' : 'bg-slate-300'}`}></span>
                                                {x.enabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 shrink-0">
                                        {checkPermission(permission.SETUP_OP_MANAGE) && data.sources[index]?.enabled && (
                                            <Link
                                                id={"manage-" + x.id}
                                                to={x.config_url}
                                                className="shrink-0 inline-block"
                                            >
                                                <Button
                                                    variant="outline"
                                                    size="xs"
                                                    icon={<ExternalLink size={12} />}
                                                    className="font-semibold"
                                                >
                                                    {content.SETUP_DASHBOARD_CONTENT.manage_btn}
                                                </Button>
                                            </Link>
                                        )}
                                        <div id={"source-wrapper-" + x.id} className="flex items-center">
                                            <Toggle
                                                id={"source-" + x.id}
                                                checked={x.enabled}
                                                onChange={() => handleOnChangeSource(x, index)}
                                                disabled={!checkPermission(permission.SETUP_UPDATE)}
                                                ariaLabel={`Toggle ${x.name}`}
                                                title={x.enabled ? `Disable ${x.name}` : `Enable ${x.name}`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>

                {/* Destinations Column */}
                <Card variant="default" padding="none" className="border border-border-subtle shadow-card flex flex-col p-6 bg-white">
                    <div className="flex items-center justify-between pb-4 mb-4 border-b border-border-subtle/80">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary-soft border border-primary/20 text-primary flex items-center justify-center shadow-xs">
                                <Send className="w-5 h-5 text-current" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-stride-heading">
                                    {content.SETUP_DASHBOARD_CONTENT.destinations}
                                </h2>
                                <p className="text-xs text-stride-muted">
                                    Target delivery platforms and delivery endpoints
                                </p>
                            </div>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-soft text-primary border border-primary/20">
                            {activeDestinationsCount} Active
                        </span>
                    </div>

                    <div className="flex flex-col gap-3">
                        {/* Manual Destination (Fixed / Disabled) */}
                        <div
                            className="transition-all duration-150 flex items-center justify-between gap-4"
                            aria-disabled="true"
                        >
                            <div className="flex items-center gap-3.5">
                                <div className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 border bg-slate-100 text-slate-700 border-slate-200">
                                    MN
                                </div>
                                <div>
                                    <p className="capitalize font-semibold text-stride-heading text-sm">
                                        {content.SETUP_DASHBOARD_CONTENT.manual}
                                    </p>
                                    <span className="text-xs text-stride-muted">
                                        Direct manual deployment
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/80">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                    Always On
                                </span>
                                <div id="destination-wrapper-">
                                    <Toggle
                                        id="destination-"
                                        checked={true}
                                        disabled={true}
                                        ariaLabel="Manual destination is always active"
                                        title="Manual destination is always active"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Dynamic Destinations */}
                        {data.destinations?.map((x, index) => {
                            const meta = getProviderMeta(x.name);
                            return (
                                <div
                                    key={x.id}
                                    data-testid={`destination-card-${x.name.toLowerCase().replace(/\s+/g, '-')}`}
                                    className={`transition-all duration-150 flex items-center justify-between gap-4`}
                                >
                                    <div className="flex items-center gap-3.5 min-w-0">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 border ${meta.color}`}>
                                            {meta.badge}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="capitalize font-semibold text-stride-heading text-sm truncate">
                                                {x.name}
                                            </p>
                                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold mt-0.5 ${
                                                x.enabled ? 'text-emerald-700' : 'text-stride-muted'
                                            }`}>
                                                <span className={`w-2 h-2 rounded-full ${x.enabled ? 'bg-emerald-500 shadow-xs' : 'bg-slate-300'}`}></span>
                                                {x.enabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 shrink-0">
                                        {checkPermission(permission.SETUP_OP_MANAGE) && data.destinations[index]?.enabled && (
                                            <Link
                                                id={"manage-" + x.id}
                                                to={x.config_url}
                                                className="shrink-0 inline-block"
                                            >
                                                <Button
                                                    variant="outline"
                                                    size="xs"
                                                    icon={<ExternalLink size={12} />}
                                                    className="font-semibold"
                                                >
                                                    {content.SETUP_DASHBOARD_CONTENT.manage_btn}
                                                </Button>
                                            </Link>
                                        )}
                                        <div id={"destination-wrapper-" + x.id} className="flex items-center">
                                            <Toggle
                                                id={"destination-" + x.id}
                                                checked={x.enabled}
                                                onChange={() => handleOnChangeDestination(x, index)}
                                                disabled={!checkPermission(permission.SETUP_UPDATE)}
                                                ariaLabel={`Toggle ${x.name}`}
                                                title={x.enabled ? `Disable ${x.name}` : `Enable ${x.name}`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>

            </div>
        </main>
    );
};

export default Setup;
